import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Collapse, Input, Row, message, Skeleton } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../../../react-auth0-spa";
import config from "../../../../api_config.json";
import { useAppContext } from "../../../../context";
import {
  dbQueryResultsGroupedItems,
  dbQueryResultsGroupedProducts,
} from "./Utils/dbQueryResultsGrouped";
import FeatureGroup from "./FeatureGroup";
import CollapsePanel from "antd/lib/collapse/CollapsePanel";
import { findOrgModules } from "../../../../graphql/controllers/fetchOrgModules";

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const getOrgModuleNodes = (orgModules) => {
  if (Array.isArray(orgModules)) {
    return orgModules;
  }

  return (
    orgModules?.org_modules?.edges?.map((edge) => edge.node).filter(Boolean) ||
    orgModules?.data?.org_modules?.edges?.map((edge) => edge.node).filter(Boolean) ||
    []
  );
};

const buildIncludedModuleIds = (orgModules) => {
  const ids = new Set();

  getOrgModuleNodes(orgModules).forEach((orgModule) => {
    const module = orgModule?.module_pointer;
    if (module?.objectId) {
      ids.add(module.objectId);
    }

    module?.included_service_relation?.edges?.forEach((edge) => {
      if (edge?.node?.objectId) {
        ids.add(edge.node.objectId);
      }
    });
  });

  return ids;
};

function checkIfIncluded(item, licence, includedModuleIds) {
  if (!licence || licence === "module") {
    const itemModuleId = item.module_pointer?.objectId;
    if (!itemModuleId) return false;
    return includedModuleIds.has(itemModuleId);
  }
  return item.plans?.some((plan) => plan.value === licence);
}

const OrgFeatures = ({ orgId, org }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const [graphql, setGraphql] = useState({});
  const [groupedFeatures, setGroupedFeature] = useState(undefined);
  const [features, setFeatures] = useState(undefined);
  const [disabledFeatures, setDisabledFeatures] = useState({});
  const [orgModules, setOrgModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const env = useMemo(() => (isProd ? "prod" : "dev"), [isProd]);

  const filteredGroupedFeatures = useMemo(() => {
    if (!groupedFeatures || !searchText.trim()) return groupedFeatures;

    const lowerSearch = searchText.toLowerCase().trim();
    const filtered = {};

    for (const product of Object.keys(groupedFeatures)) {
      const filteredProduct = {};

      for (const groupKey of Object.keys(groupedFeatures[product])) {
        const groupItems = groupedFeatures[product][groupKey];
        const showItem = groupItems.find((item) => item.action === "show");

        // Check if group name/label matches
        const groupLabel = showItem?.label || groupKey;
        const groupMatches = groupLabel.toLowerCase().includes(lowerSearch) ||
          groupKey.toLowerCase().includes(lowerSearch);

        // Check if any item in the group matches
        const itemMatches = groupItems.some(
          (item) =>
            item.label?.toLowerCase().includes(lowerSearch) ||
            item.action?.toLowerCase().includes(lowerSearch) ||
            item.description?.toLowerCase().includes(lowerSearch)
        );

        if (groupMatches || itemMatches) {
          filteredProduct[groupKey] = groupItems;
        }
      }

      if (Object.keys(filteredProduct).length > 0) {
        filtered[product] = filteredProduct;
      }
    }

    return filtered;
  }, [groupedFeatures, searchText]);

  const fetchFeatures = useCallback(
    async (graphql) => {
      try {
        const token = await getTokenSilently();

        // Using GraphQL to fetch features
        const query = `
      query FindFeatures {
        findFeatures(first: 10000, order: name_ASC) {
          edges {
            node {
              objectId
              name
              label
              description
              default
              products {
                ... on Element {
                  value
                }
              }
              plans {
                ... on Element {
                  value
                }
              }
              module_pointer {
                objectId
                name
              }
            }
          }
        }
      }`;

        const response = await fetch(
          `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ query }),
          }
        );

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        let orgModules;
        let includedModuleIds = new Set();
        if (!org.tipo_licenza || org.tipo_licenza === "module") {
          orgModules = await findOrgModules(graphql, orgId);
          setOrgModules(orgModules);
          includedModuleIds = buildIncludedModuleIds(orgModules);
        }

        // Transform the GraphQL response to match the format expected by the grouping functions
        const featuresData = result.findFeatures.edges.map((edge) => {
          const node = edge.node;
          const included = checkIfIncluded(
            node,
            org.tipo_licenza,
            includedModuleIds
          );
          return {
            objectId: node.objectId,
            name: node.name,
            label: node.label,
            description: node.description,
            products: node.products
              ? node.products.map((product) => product.value)
              : [],
            plans: node.plans ? node.plans.map((plan) => plan.value) : [],
            module: node.module_pointer,
            disabled: !included && !node.default,
            included: included,
          };
        });

        const featuresGrouped = featuresData
          .filter((item) => item.name !== undefined)
          .reduce(dbQueryResultsGroupedProducts, {});

        for (const product of Object.keys(featuresGrouped)) {
          featuresGrouped[product] = featuresGrouped[product].reduce(
            dbQueryResultsGroupedItems,
            {}
          );
        }

        setGroupedFeature(featuresGrouped);
        setFeatures(featuresData);
        return featuresData;
      } catch (error) {
        console.error("Error fetching features:", error);
        message.error("Errore nel caricamento delle funzionalità");
      }
    },
    [getTokenSilently, isProd, org.tipo_licenza, orgId]
  );

  const fetchFeaturesPreferences = useCallback(
    async (allFeatures) => {
      try {
        const token = await getTokenSilently();

        const query = `
        query FindFeaturePreferences($orgId: ID!) {
          features_preferences(
            first: 10000,
            where: {
              org_pointer: { have: { objectId: { equalTo: $orgId } } },
              user_pointer: { exists: false },
            }
          ) {
            edges {
              node {
                objectId
                key
                product
                disabled
              }
            }
          }
        }
      `;

        const response = await fetch(
          `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              query,
              variables: { orgId },
            }),
          }
        );

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        // Transform the edges/node structure to a flat array
        const preferencesData =
          result.features_preferences?.edges?.map((edge) => edge.node) || [];

        for (const feature of (allFeatures || []).filter(
          (item) => item.disabled
        )) {
          if (!preferencesData.some((item) => item.key === feature.name)) {
            //This creates a fake feature pref based on features not included in org plan

            const shouldBeDisabled = !preferencesData.some(
              (item) =>
                item.key.split(".")[0] === feature.name.split(".")[0] &&
                item.key.split(".")[1] === "show" &&
                item.disabled === false
            );

            const products = feature.products || [];
            for (const product of products) {
              const featurePref = {
                key: feature.name,
                disabled: shouldBeDisabled,
                product,
              };
              preferencesData.push(featurePref);
            }
          }
        }

        // Initialize an empty object to avoid undefined errors
        const disabledFeaturesGrouped = preferencesData
          .filter((item) => item.key !== undefined && item.disabled === true)
          .reduce(dbQueryResultsGroupedProducts, {});

        for (const product of Object.keys(disabledFeaturesGrouped)) {
          disabledFeaturesGrouped[product] = disabledFeaturesGrouped[
            product
          ].reduce(dbQueryResultsGroupedItems, {});
        }

        setDisabledFeatures(disabledFeaturesGrouped);
      } catch (error) {
        console.error("Error fetching feature preferences:", error);
        message.error(
          "Errore nel caricamento delle preferenze delle funzionalità"
        );
        // Initialize with empty object to prevent errors
        setDisabledFeatures({});
      }
    },
    [getTokenSilently, isProd, orgId]
  );

  useEffect(() => {
    const run = async () => {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql`;
      setGraphql({
        url: graphqlUrl,
        token,
      });
    };
    run();
  }, [env, getTokenSilently]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    if (!graphql.token) return;
    const allFeatures = await fetchFeatures(graphql);
    await fetchFeaturesPreferences(allFeatures);
    setLoading(false);
  }, [fetchFeatures, fetchFeaturesPreferences, graphql]);

  const setOrgFeaturePreference = async (data) => {
    try {
      const token = await getTokenSilently();

      const response = await fetch(
        `${
          config.baseUrl[isProd ? "prod" : "dev"]
        }/setFeaturePreference/${orgId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error("Error setting feature preference:", error);
      throw error;
    }
  };

  const handleClick = async (data) => {
    if (data.key && data.disabled !== undefined) {
      message.loading({
        content: "Funzionalità in aggiornamento..",
        key: "loading",
        duration: 0,
      });

      const payload = {
        ...data,
      };
      try {
        await setOrgFeaturePreference(payload);
        await fetchFeaturesPreferences(features);
        message.destroy("loading");
        message.success("Funzionalità aggiornata con successo");
      } catch (error) {
        message.destroy("loading");
        message.error("Errore: Funzionalità non aggiornata");
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="org-features-container">
      {loading && <Skeleton active />}
      {!loading && groupedFeatures && (
        <>
          <Input
            placeholder="Cerca funzionalità..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ marginBottom: 16 }}
          />
          <Row>
            <Col span={24}>
              <Collapse defaultActiveKey={["eterno", "giotto"]}>
                {filteredGroupedFeatures &&
                  Object.keys(filteredGroupedFeatures)
                    .sort((a, b) => a.localeCompare(b))
                    .map((product) => {
                      return (
                        <CollapsePanel
                          header={capitalizeFirstLetter(product)}
                          key={product}
                        >
                          <Row gutter={[16, 16]}>
                            {Object.keys(filteredGroupedFeatures[product]).map(
                              (key) => {
                                // Create an array from the disabledItems object if needed
                                const disabledItemsForGroup =
                                  disabledFeatures[product] &&
                                  disabledFeatures[product][key]
                                    ? Object.values(
                                        disabledFeatures[product][key]
                                      )
                                    : [];

                                return (
                                  <FeatureGroup
                                    key={key}
                                    group={key}
                                    product={product}
                                    items={filteredGroupedFeatures[product][key]}
                                    disabledItems={disabledItemsForGroup}
                                    handleClick={handleClick}
                                    orgLicence={org.tipo_licenza}
                                    orgModules={orgModules}
                                  />
                                );
                              }
                            )}
                          </Row>
                        </CollapsePanel>
                      );
                    })}
              </Collapse>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default OrgFeatures;
